import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'

import styled from 'styled-components'

import Spinner from 'components/Spinner'

import CustomerDetails from 'pages/Customer/CustomerDetails'
import DeviceTabs from 'pages/shared/DeviceTabs'
import NotFound from 'pages/NotFound'

import useCustomer from 'hooks/useCustomer'
import { useSessionContext } from 'hooks/SessionContext'
import useSearchParams from 'hooks/useSearchParams'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import { customerDisplayName } from 'helpers/formatters'

import { NoData, PageHeader, PageTitle, screenSize, SinglePanePage } from 'styles/shared'

const Customer = () => {
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now())

  const { enabledFeatures } = useFeaturesContext()
  const isSystemDashboardEnabled = enabledFeatures.includes('system_dashboard')

  const navigate = useNavigate()
  const { session: { dealerId } } = useSessionContext()
  const { customerId, deviceId } = useParams()
  const thermostatRthDate = useSearchParams('thermostatRthDate')
  const awairRthDate = useSearchParams('awairRthDate')
  const { customer, isCustomerLoaded } = useCustomer(dealerId, customerId, refreshTimestamp)
  const { pathname } = useLocation()
  const [seletedDeviceId, setSeletedDeviceId] = useState(deviceId)

  useEffect(() => {
    if (!deviceId || !customer) return

    setSeletedDeviceId((prevDeviceId) => {
      if (prevDeviceId === deviceId) return prevDeviceId
      return deviceId
    })

    const queryString = [
      thermostatRthDate && `thermostatRthDate=${thermostatRthDate}`,
      awairRthDate && `awairRthDate=${awairRthDate}`
    ].filter(Boolean).join('&')

    navigate(
      `/customers/${customerId}/systems/${getDeviceLink(deviceId)}${queryString ? `?${queryString}` : ''}`,
      { replace: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId])

  const getDeviceLink = useCallback((deviceId: string) => {
    if (isSystemDashboardEnabled) {
      return `${deviceId}/dashboard`
    }
    return `${deviceId}`
  }, [isSystemDashboardEnabled])

  useEffect(() => {
    if (isCustomerLoaded) {
      if (customer?.devices?.length && !customer?.devices?.find((device) => device.deviceId === seletedDeviceId)) {
        // use replace instead of push to avoid back navigation issue. replace "/customers/customerId/" with "/customers/customerId/systems/deviceId"
        navigate(`/customers/${customerId}/systems/${getDeviceLink(customer.devices[0].deviceId)}`, { replace: true })
      } else if (!customer?.devices?.length && pathname !== '/customers/' + customerId) {
        navigate(`/customers/${customerId}`)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.devices, customerId, navigate, isCustomerLoaded, pathname])

  const navigateToDeviceId = useCallback((deviceId: string) => {
    setSeletedDeviceId(deviceId)
    navigate(`/customers/${customerId}/systems/${getDeviceLink(deviceId)}`)
  }, [customerId, navigate, getDeviceLink])

  const refreshPage = () => {
    setRefreshTimestamp(Date.now())
  }

  const onSaveCallback = (deviceId: string) => {
    refreshPage()
    navigateToDeviceId(deviceId)
  }

  const device = customer?.devices?.find(device => device.deviceId === seletedDeviceId)
  const customerName = customerDisplayName(customer?.firstName, customer?.lastName, customer?.companyName)

  return (
    <S.Container>
      <S.PageHeader>
        <PageTitle>Customers</PageTitle>
      </S.PageHeader>

      {!isCustomerLoaded && <Spinner variant="page" />}

      {isCustomerLoaded && !customer && <NotFound />}
      {isCustomerLoaded && customer && (
      <SinglePanePage>
        <S.CustomerDetails
          customer={customer}
          activeDevice={device}
          onSaveCallback={onSaveCallback}
          devices={customer.devices || []}
        />

        {seletedDeviceId &&
        <S.DeviceTabs
          customerName={customerName}
          activeDeviceId={seletedDeviceId}
          devices={customer.devices || []}
          onActiveDeviceChangeCallback={navigateToDeviceId}
          refreshPageCallback={refreshPage}
        />
      }

        {!customer.devices?.length &&
        <NoData>
          <h1>NO SYSTEMS</h1>
          <h2>CLICK "ASSIGN SYSTEM" TO ASSIGN A SYSTEM TO THIS CUSTOMER.</h2>
        </NoData>
      }
      </SinglePanePage>
      )}
    </S.Container>

  )
}

export default Customer

const S = {
  Container: styled.div`
    width: 100%;
  `,
  PageHeader: styled(PageHeader)`
    @media ${screenSize.small} {
      display: none;
    }
  `,
  CustomerDetails: styled(CustomerDetails)`
    margin-bottom: 15px;

    @media ${screenSize.small} {
      margin-top: 0;
    }
  `,
  DeviceTabs: styled(DeviceTabs)`
    margin-left: 2px;
  `
}
